import { PERMISSIONS } from "@utils/permissions";

// Chiavi i18n di titolo e descrizione che variano in base ai permessi di
// lettura su utenti, gruppi statici e gruppi dinamici:
// - card della sidebar (namespace "sidebar", cards.users-groups.*);
// - sezione "Ultimi utenti e gruppi" di Il mio spazio (namespace "my-space",
//   latest-users-and-groups.*).
// I valori di USERS_GROUPS_TITLE_KEY e USERS_GROUPS_DESCRIPTION_PART sono anche
// le chiavi in src/translation/{it,en}.json: se cambiano qui, vanno allineati
// anche nei file di traduzione.
const USERS_GROUPS_TITLES_PREFIX = "cards.users-groups.titles";
const USERS_GROUPS_DESCRIPTIONS_PREFIX = "cards.users-groups.descriptions";
const MY_SPACE_LATEST_TITLES_PREFIX = "latest-users-and-groups.titles";
const MY_SPACE_LATEST_SUBTITLES_PREFIX = "latest-users-and-groups.subtitles";

export const USERS_GROUPS_TITLE_KEY = {
  both: "both",
  users: "users",
  groups: "groups",
} as const;

export const USERS_GROUPS_DESCRIPTION_PART = {
  users: "users",
  static: "static",
  dynamic: "dynamic",
} as const;

const USERS_GROUPS_DESCRIPTION_SEPARATOR = "-";

export interface UsersGroupsReadPermissions {
  hasUsers: boolean;
  hasStaticGroups: boolean;
  hasDynamicGroups: boolean;
}

export const getUsersGroupsReadPermissions = (
  permissions: string[] = [],
): UsersGroupsReadPermissions => ({
  hasUsers: permissions.includes(PERMISSIONS.users.read),
  hasStaticGroups: permissions.includes(PERMISSIONS.groups.static.read),
  hasDynamicGroups: permissions.includes(PERMISSIONS.groups.dynamic.read),
});

const getTitleVariant = ({
  hasUsers,
  hasStaticGroups,
  hasDynamicGroups,
}: UsersGroupsReadPermissions): string => {
  const hasGroups = hasStaticGroups || hasDynamicGroups;
  if (hasUsers && hasGroups) return USERS_GROUPS_TITLE_KEY.both;
  if (hasUsers) return USERS_GROUPS_TITLE_KEY.users;
  return USERS_GROUPS_TITLE_KEY.groups;
};

// L'ordine delle parti (users, static, dynamic) deve coincidere con quello
// delle chiavi nelle sezioni "descriptions" e "subtitles" delle traduzioni.
const getDescriptionVariant = ({
  hasUsers,
  hasStaticGroups,
  hasDynamicGroups,
}: UsersGroupsReadPermissions): string =>
  [
    hasUsers && USERS_GROUPS_DESCRIPTION_PART.users,
    hasStaticGroups && USERS_GROUPS_DESCRIPTION_PART.static,
    hasDynamicGroups && USERS_GROUPS_DESCRIPTION_PART.dynamic,
  ]
    .filter(Boolean)
    .join(USERS_GROUPS_DESCRIPTION_SEPARATOR);

export const getUsersGroupsTitleKey = (
  permissions: UsersGroupsReadPermissions,
): string => `${USERS_GROUPS_TITLES_PREFIX}.${getTitleVariant(permissions)}`;

export const getUsersGroupsDescriptionKey = (
  permissions: UsersGroupsReadPermissions,
): string =>
  `${USERS_GROUPS_DESCRIPTIONS_PREFIX}.${getDescriptionVariant(permissions)}`;

export const getMySpaceLatestUsersGroupsTitleKey = (
  permissions: UsersGroupsReadPermissions,
): string => `${MY_SPACE_LATEST_TITLES_PREFIX}.${getTitleVariant(permissions)}`;

export const getMySpaceLatestUsersGroupsSubtitleKey = (
  permissions: UsersGroupsReadPermissions,
): string =>
  `${MY_SPACE_LATEST_SUBTITLES_PREFIX}.${getDescriptionVariant(permissions)}`;
